import countries from "../assets/json/countries.json";

export const SELECTED_ESIM_COUNTRY_STORAGE_KEY = "selectedEsimCountry";
export const ESIM_PAYMENT_METHODS = [
  {
    value: "Wallet",
    label: "Wallet",
    description: "Use your Intraverse wallet balance for an instant payment.",
  },
  {
    value: "Card",
    label: "Pay by Card",
    description: "Continue to pay by card to complete payment with your card.",
  },
];

export function flagFromCode(code) {
  if (!code || String(code).length !== 2) {
    return "🏳️";
  }

  const upperCode = String(code).toUpperCase();
  return String.fromCodePoint(
    ...[...upperCode].map((char) => 127397 + char.charCodeAt(0)),
  );
}

export function formatPackagePrice(amount, currencyCode = "USD") {
  const normalizedAmount = normalizeNumber(amount);
  const normalizedCurrency = resolveText(currencyCode).toUpperCase() || "USD";

  if (!Number.isFinite(normalizedAmount)) {
    return "Price unavailable";
  }

  const minimumFractionDigits =
    normalizedCurrency === "NGN" && Number.isInteger(normalizedAmount) ? 0 : 2;
  const formattedAmount = new Intl.NumberFormat("en-US", {
    minimumFractionDigits,
    maximumFractionDigits: 2,
  }).format(normalizedAmount);

  return `${currencySymbolFor(normalizedCurrency)}${formattedAmount} ${normalizedCurrency}`;
}

export function extractDataArray(source, preferredPaths = []) {
  for (const path of preferredPaths) {
    const value = getValueAtPath(source, path);
    if (Array.isArray(value)) {
      return value;
    }
  }

  const firstArray = findFirstArray(source);
  return Array.isArray(firstArray) ? firstArray : [];
}

export function normalizeEsimCountry(country) {
  if (!country || typeof country !== "object") {
    return null;
  }

  const initialCode = resolveText(
    country.code ||
      country.countryCode ||
      country.alpha2 ||
      country.iso2 ||
      country.country,
  )?.toUpperCase();

  const initialTitle =
    resolveText(country.title || country.name || country.countryName) || "";
  const countryLookup =
    getLocalCountryByCode(initialCode) || getLocalCountryByName(initialTitle);
  const code = initialCode || countryLookup?.code;
  const title = initialTitle || countryLookup?.title || code;

  if (!code || !title) {
    return null;
  }

  const packageCount = normalizeInteger(
    country.packageCount ||
      country.packagesCount ||
      country.package_count ||
      country.totalPackages,
  );

  const amountPrice = normalizeNumber(
    country.amount ||
      country.cheapestAmount ||
      country.cheapest_amount ||
      country.startingAmount ||
      country.starting_amount,
  );
  const cheapestPrice = Number.isFinite(amountPrice)
    ? amountPrice
    : normalizeNumber(
        country.cheapestPrice ||
          country.cheapest_price ||
          country.startingPrice ||
          country.starting_price ||
          country.lowestPrice ||
          country.price,
      );
  const currency = Number.isFinite(amountPrice)
    ? "NGN"
    : resolveText(
        country.currency ||
          country.currencyCode ||
          country.baseCurrency ||
          country.displayCurrency,
      ) || "USD";

  return {
    ...country,
    code,
    slug:
      resolveText(country.slug) ||
      title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, ""),
    title,
    name: title,
    image:
      resolveText(
        country.image?.url ||
          country.imageUrl ||
          country.image ||
          country.flag ||
          country.icon,
      ) ||
      countryLookup?.image ||
      "",
    packageCount,
    cheapestPrice,
    currency,
  };
}

export function normalizeEsimPopularCountry(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const rawCountry = ensureObject(entry.country);
  const country = normalizeEsimCountry({
    ...rawCountry,
    image: rawCountry.image?.url || rawCountry.image,
  });

  if (!country) {
    return null;
  }

  const cheapestPackage = normalizeEsimPackage({
    ...ensureObject(entry.cheapestPackage),
    country: country.title,
    countryCode: country.code,
    countryImage: country.image,
  });

  return {
    id: country.code,
    country,
    packageCount: normalizeInteger(entry.packageCount) || 0,
    cheapestPackage,
    raw: entry,
  };
}

export function normalizeEsimPackage(pkg, index = 0) {
  if (!pkg || typeof pkg !== "object") {
    return null;
  }

  const raw = pkg?.raw && typeof pkg.raw === "object" ? pkg.raw : pkg;
  const operatorSource =
    raw.operator && typeof raw.operator === "object"
      ? raw.operator
      : pkg.operator && typeof pkg.operator === "object"
        ? pkg.operator
        : {};

  const day = normalizeInteger(
    pkg.day || pkg.days || pkg.validity || pkg.durationDays || raw.day,
  );
  const unlimited = normalizeBoolean(
    pkg.unlimited ||
      pkg.isUnlimited ||
      pkg.is_unlimited ||
      raw.unlimited ||
      raw.isUnlimited ||
      raw.is_unlimited,
  );
  const dataLabel = formatPackageData(pkg.data || raw.data, unlimited);
  const farePrice = normalizeNumber(
    pkg.farePrice ||
      pkg.fare_price ||
      pkg.salePrice ||
      pkg.totalPrice ||
      raw.farePrice ||
      raw.fare_price ||
      raw.salePrice ||
      raw.totalPrice,
  );
  const amountPrice = normalizeNumber(
    pkg.amount ||
      pkg.packageAmount ||
      pkg.displayAmount ||
      raw.amount ||
      raw.packageAmount ||
      raw.displayAmount,
  );
  const basePrice = normalizeNumber(
    pkg.price ||
      pkg.originalPrice ||
      pkg.basePrice ||
      raw.price ||
      raw.originalPrice ||
      raw.basePrice ||
      farePrice,
  );
  const displayPrice = Number.isFinite(amountPrice)
    ? amountPrice
    : Number.isFinite(farePrice)
      ? farePrice
      : basePrice;
  const currency = Number.isFinite(amountPrice)
    ? "NGN"
    : resolveText(
        pkg.currency ||
          pkg.currencyCode ||
          raw.currency ||
          raw.currencyCode ||
          raw.baseCurrency,
      ) || "USD";
  const title =
    resolveText(pkg.title || pkg.name || raw.title || raw.name) ||
    [dataLabel, day ? `${day} day${day === 1 ? "" : "s"}` : null]
      .filter(Boolean)
      .join(" / ");

  const validityLabel =
    resolveText(pkg.validityLabel || raw.validityLabel) ||
    (day ? `${day} day${day === 1 ? "" : "s"}` : "Flexible duration");
  const durationLabel =
    resolveText(pkg.durationLabel || raw.durationLabel) || validityLabel;

  const countryText = resolveText(
    pkg.country ||
      raw.country ||
      raw.countryName ||
      raw.coverageCountry ||
      raw.coverage_country,
  );
  const countryLookup =
    getLocalCountryByCode(
      resolveText(
        pkg.countryCode ||
          raw.countryCode ||
          raw.country_code ||
          raw.code ||
          raw.iso2,
      ),
    ) || getLocalCountryByName(countryText);
  const countryCode =
    resolveText(
      pkg.countryCode ||
        raw.countryCode ||
        raw.country_code ||
        raw.code ||
        raw.iso2,
    )?.toUpperCase() || countryLookup?.code || "";

  const coverageList = uniqueTextList(
    pkg.coverages,
    raw.coverages,
    pkg.coverage,
    raw.coverage,
    raw.coverageCountries,
    raw.coverage_countries,
  );
  const supportedNetworks = uniqueTextList(
    pkg.supportedNetworks,
    raw.supportedNetworks,
    raw.supported_networks,
    raw.networkProviders,
    raw.network_providers,
    raw.networks,
    raw.coverages?.map((item) => item?.networks),
    operatorSource.networks,
  );
  const networkType =
    resolveText(
      pkg.networkType ||
        raw.networkType ||
        raw.network_type ||
        raw.access ||
        raw.technology,
    ) || inferNetworkTypeFromText([...supportedNetworks, ...coverageList]);
  const packageType = resolveText(
    pkg.packageType || raw.packageType || raw.package_type || raw.type,
  );
  const esimType = resolveText(
    pkg.esimType || raw.esimType || raw.esim_type || raw.simType,
  );
  const planType = resolveText(
    pkg.planType || raw.planType || raw.plan_type || raw.plan,
  );
  const operator = resolveText(
    typeof pkg.operator === "string"
      ? pkg.operator
      : operatorSource.name ||
          operatorSource.title ||
          operatorSource.operator ||
          operatorSource.label ||
          raw.operator ||
          raw.operatorName,
  );
  const supplier = resolveText(pkg.supplier || raw.supplier || raw.vendor);
  const packageId =
    resolveText(
      pkg.packageId ||
        pkg.package_id ||
        raw.packageId ||
        raw.package_id ||
        pkg.id ||
        pkg._id ||
        raw.id ||
        raw._id ||
        raw.slug,
    ) ||
    `${resolveText(raw.code || raw.country || raw.slug) || "pkg"}-${
      day || "na"
    }-${dataLabel || index}-${index}`;
  const activationPolicy = resolveText(
    pkg.activationPolicy ||
      raw.activationPolicy ||
      raw.activation_policy ||
      raw.activation,
  );
  const fairUsagePolicy = resolveText(
    pkg.fairUsagePolicy ||
      raw.fairUsagePolicy ||
      raw.fair_usage_policy ||
      raw.fup,
  );
  const rechargeability = resolveText(
    pkg.rechargeability || raw.rechargeability || raw.rechargeable,
  );
  const topupMetadata =
    pkg.topupMetadata ||
    raw.topupMetadata ||
    raw.topup_metadata ||
    raw.topupInfo ||
    null;
  const topUpSupported = normalizeBoolean(
    pkg.topUpSupported ||
      pkg.topupSupported ||
      pkg.topUp ||
      pkg.topup ||
      pkg.canTopUp ||
      pkg.has_topup ||
      raw.topUpSupported ||
      raw.topupSupported ||
      raw.topUp ||
      raw.topup ||
      raw.canTopUp ||
      raw.has_topup,
  );

  return {
    ...pkg,
    id:
      resolveText(pkg.id || pkg._id || raw.id || raw._id || raw.slug) ||
      packageId,
    packageId,
    title,
    data: dataLabel,
    dataAmount: dataLabel,
    day,
    validity: validityLabel,
    durationLabel,
    unlimited,
    amount: amountPrice,
    price: basePrice,
    farePrice,
    displayPrice,
    unitPrice: Number.isFinite(amountPrice)
      ? amountPrice
      : normalizeNumber(pkg.unitPrice || raw.unitPrice || raw.unit_price) ||
        displayPrice,
    bookingFee: normalizeNumber(
      pkg.bookingFee || raw.bookingFee || raw.booking_fee,
    ),
    grandTotal: Number.isFinite(amountPrice)
      ? amountPrice
      : normalizeNumber(pkg.grandTotal || raw.grandTotal || raw.grand_total),
    quantity: normalizeInteger(pkg.quantity || raw.quantity) || 1,
    currency,
    operator,
    operatorLogo: resolveText(
      operatorSource.logo ||
        operatorSource.image ||
        raw.operatorLogo ||
        raw.operator_logo,
    ),
    operatorInfo: resolveText(operatorSource.info || raw.operatorInfo),
    operatorOtherInfo: resolveText(
      operatorSource.otherInfo || raw.operatorOtherInfo,
    ),
    supplier,
    networkType,
    packageType,
    esimType,
    planType,
    coverage: formatCoverage(coverageList.length ? coverageList : countryText),
    coverages: coverageList,
    supportedNetworks,
    country: countryText || countryLookup?.title || "",
    countryCode,
    countryFlag: flagFromCode(countryCode),
    countryImage:
      resolveText(
        raw.countryImage ||
          raw.country_image ||
          raw.country?.image?.url ||
          raw.country?.image ||
          raw.image,
      ) ||
      countryLookup?.image ||
      "",
    topUpSupported,
    hasTopup: topUpSupported,
    topupMetadata,
    fairUsagePolicy,
    activationPolicy,
    rechargeability,
    qrInstallation: resolveText(
      pkg.qr_installation ||
        raw.qr_installation ||
        raw.qrInstallation ||
        raw.installation_qr,
    ),
    manualInstallation: resolveText(
      pkg.manual_installation ||
        raw.manual_installation ||
        raw.manualInstallation ||
        raw.installation_manual,
    ),
    status: resolveText(pkg.status || raw.status),
    additionalInfo: uniqueTextList(
      pkg.description,
      raw.description,
      raw.info,
      raw.notes,
      raw.additionalInfo,
      raw.additional_info,
    ),
    raw,
  };
}

export function getEsimPackageResponseField(source, fieldName) {
  if (!source || typeof source !== "object" || !fieldName) {
    return undefined;
  }

  const raw = source.raw && typeof source.raw === "object" ? source.raw : null;
  if (raw && Object.prototype.hasOwnProperty.call(raw, fieldName)) {
    return raw[fieldName];
  }

  if (Object.prototype.hasOwnProperty.call(source, fieldName)) {
    return source[fieldName];
  }

  return undefined;
}

export function getEsimPackageGroupingType(source) {
  const data = getEsimPackageResponseField(source, "data");
  const voice = getEsimPackageResponseField(source, "voice");
  const text = getEsimPackageResponseField(source, "text");

  if (typeof data === "string" && data.trim() === "Unlimited") {
    return "unlimited";
  }

  if (voice == null && text == null) {
    return "standard";
  }

  return "data-voice-text";
}

export function groupEsimPackages(packages = []) {
  const groupedPackages = packages.reduce((groups, pkg) => {
    const normalizedPackage =
      pkg?.packages && pkg?.durationLabel
        ? null
        : normalizeEsimPackage(pkg, groups.length);

    if (!normalizedPackage) {
      return groups;
    }

    const groupLabel = normalizedPackage.durationLabel || "Flexible duration";
    const existingGroup = groups.find((group) => group.durationLabel === groupLabel);

    if (existingGroup) {
      existingGroup.packages.push(normalizedPackage);
      return groups;
    }

    return [
      ...groups,
      {
        durationLabel: groupLabel,
        day: normalizedPackage.day,
        packages: [normalizedPackage],
      },
    ];
  }, []);

  return groupedPackages
    .map((group) => ({
      ...group,
      packages: [...group.packages].sort(
        (left, right) =>
          (Number.isFinite(left.displayPrice) ? left.displayPrice : Infinity) -
          (Number.isFinite(right.displayPrice) ? right.displayPrice : Infinity),
      ),
    }))
    .sort((left, right) => {
      if (!left.day && !right.day) return 0;
      if (!left.day) return 1;
      if (!right.day) return -1;
      return left.day - right.day;
    });
}

export function deriveEsimCountryMeta(country, packages = []) {
  const normalizedPackages = packages
    .map((pkg, index) => normalizeEsimPackage(pkg, index))
    .filter(Boolean);

  const cheapestPackage = normalizedPackages.reduce((lowestPackage, pkg) => {
    if (!Number.isFinite(pkg.displayPrice)) {
      return lowestPackage;
    }

    if (!lowestPackage || !Number.isFinite(lowestPackage.displayPrice)) {
      return pkg;
    }

    return pkg.displayPrice < lowestPackage.displayPrice ? pkg : lowestPackage;
  }, null);

  const packageNetworkType =
    normalizedPackages.find((pkg) => pkg.networkType)?.networkType || "";
  const hasLivePackagePrice = Number.isFinite(cheapestPackage?.displayPrice);
  const networkType =
    packageNetworkType ||
    (normalizedPackages.some((pkg) => /5g/i.test(pkg.coverage || ""))
      ? "5G"
      : normalizedPackages.some((pkg) => /4g/i.test(pkg.coverage || ""))
        ? "4G"
        : normalizedPackages.length
          ? "4G / 5G"
          : "Data");

  return {
    carrier:
      normalizedPackages.find((pkg) => pkg.operator)?.operator ||
      normalizedPackages.find((pkg) => pkg.supplier)?.supplier ||
      "Local network",
    networkType,
    packageCount:
      country?.packageCount || country?.package_count || normalizedPackages.length,
    startingPrice: hasLivePackagePrice
      ? cheapestPackage.displayPrice
      : Number.isFinite(country?.cheapestPrice)
        ? country.cheapestPrice
        : null,
    startingPriceCurrency:
      (hasLivePackagePrice ? cheapestPackage?.currency : country?.currency) ||
      "USD",
    topUpSupported: normalizedPackages.some((pkg) => pkg.topUpSupported),
  };
}

export function normalizeEsimBookingResponse(rawResponse, fallback = {}) {
  const raw = rawResponse && typeof rawResponse === "object" ? rawResponse : {};
  const booking = ensureObject(raw.booking || raw.order || raw.orderBooking);
  const esimBooking = ensureObject(
    raw.esimBooking || raw.esim_booking || raw.esim || raw.data,
  );
  const selectedCountry = normalizeEsimCountry(fallback.country) || null;
  const selectedPackage = normalizeEsimPackage(fallback.selectedPackage) || null;

  const packageSource = ensureObject(
    esimBooking.packageDetails ||
      esimBooking.package_details ||
      raw.packageDetails ||
      raw.package_details ||
      booking.packageDetails ||
      booking.package_details ||
      selectedPackage?.raw ||
      selectedPackage ||
      {},
  );

  const mergedPackage = normalizeEsimPackage({
    ...packageSource,
    packageId:
      packageSource.packageId ||
      packageSource.package_id ||
      esimBooking.packageId ||
      esimBooking.package_id ||
      booking.packageId ||
      selectedPackage?.packageId,
    supplier:
      packageSource.supplier || esimBooking.supplier || selectedPackage?.supplier,
    quantity:
      esimBooking.quantity || booking.quantity || selectedPackage?.quantity || 1,
    unitPrice:
      esimBooking.unitPrice ||
      esimBooking.unit_price ||
      booking.unitPrice ||
      raw.unitPrice,
    bookingFee:
      esimBooking.bookingFee ||
      esimBooking.booking_fee ||
      booking.bookingFee ||
      raw.bookingFee,
    grandTotal:
      esimBooking.grandTotal ||
      esimBooking.grand_total ||
      booking.grandTotal ||
      raw.grandTotal,
    status: esimBooking.status || booking.status || raw.status,
    qr_installation:
      esimBooking.qr_installation ||
      raw.qr_installation ||
      packageSource.qr_installation,
    manual_installation:
      esimBooking.manual_installation ||
      raw.manual_installation ||
      packageSource.manual_installation,
    country:
      packageSource.country ||
      selectedCountry?.title ||
      selectedCountry?.name ||
      selectedPackage?.country,
    countryCode:
      packageSource.countryCode ||
      packageSource.country_code ||
      selectedCountry?.code ||
      selectedPackage?.countryCode,
    countryImage:
      packageSource.countryImage ||
      packageSource.country_image ||
      selectedCountry?.image ||
      selectedPackage?.countryImage,
    operator:
      packageSource.operator || esimBooking.operator || selectedPackage?.operator,
    operatorLogo:
      packageSource.operatorLogo ||
      packageSource.operator_logo ||
      selectedPackage?.operatorLogo,
  });

  if (!mergedPackage) {
    return {
      raw,
      booking,
      esimBooking,
      packageDetails: null,
    };
  }

  const grandTotal = Number.isFinite(mergedPackage.grandTotal)
    ? mergedPackage.grandTotal
    : Number.isFinite(mergedPackage.displayPrice)
      ? mergedPackage.displayPrice
      : null;

  const packageDetails = {
    ...mergedPackage,
    unitPrice: Number.isFinite(mergedPackage.unitPrice)
      ? mergedPackage.unitPrice
      : mergedPackage.displayPrice,
    grandTotal,
    quantity: mergedPackage.quantity || 1,
    status: mergedPackage.status || "Pending",
    country: mergedPackage.country || selectedCountry?.title || "",
    countryCode: mergedPackage.countryCode || selectedCountry?.code || "",
    countryFlag: flagFromCode(
      mergedPackage.countryCode || selectedCountry?.code || "",
    ),
    countryImage:
      mergedPackage.countryImage || selectedCountry?.image || selectedPackage?.countryImage || "",
    currency: mergedPackage.currency || selectedPackage?.currency || "USD",
  };

  return {
    raw,
    booking,
    esimBooking,
    packageDetails,
  };
}

export function getEsimDisplaySections(source, country) {
  const normalizedPackage = normalizeEsimPackage(source);
  const normalizedCountry = normalizeEsimCountry(country);

  if (!normalizedPackage) {
    return [];
  }

  const sections = [
    {
      title: "Package overview",
      items: [
        { label: "Country", value: normalizedPackage.country || normalizedCountry?.title },
        { label: "Supplier", value: normalizedPackage.supplier },
        { label: "Operator", value: normalizedPackage.operator },
        { label: "Data amount", value: normalizedPackage.dataAmount },
        { label: "Validity", value: normalizedPackage.validity },
        { label: "Network type", value: normalizedPackage.networkType },
        { label: "Package type", value: normalizedPackage.packageType },
        { label: "Plan type", value: normalizedPackage.planType },
        { label: "eSIM type", value: normalizedPackage.esimType },
      ],
    },
    {
      title: "Coverage and network",
      items: [
        { label: "Coverage", value: normalizedPackage.coverage },
        {
          label: "Supported networks",
          value: formatListValue(normalizedPackage.supportedNetworks),
        },
        {
          label: "Top-up support",
          value: normalizedPackage.topUpSupported ? "Available" : "Not listed",
        },
      ],
    },
    {
      title: "Policies and usage",
      items: [
        {
          label: "Fair Usage Policy",
          value: normalizedPackage.fairUsagePolicy,
        },
        {
          label: "Activation policy",
          value: normalizedPackage.activationPolicy,
        },
        {
          label: "Rechargeability",
          value: normalizedPackage.rechargeability,
        },
        {
          label: "Top-up details",
          value: formatStructuredText(normalizedPackage.topupMetadata),
        },
      ],
    },
    {
      title: "More to know",
      items: [
        { label: "Operator information", value: normalizedPackage.operatorInfo },
        {
          label: "Additional operator information",
          value: normalizedPackage.operatorOtherInfo,
        },
        {
          label: "Additional package information",
          value: formatListValue(normalizedPackage.additionalInfo, " • "),
        },
      ],
    },
  ];

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasDisplayValue(item.value)),
    }))
    .filter((section) => section.items.length > 0);
}

export function getEsimInstallationSections(source) {
  const normalizedPackage = normalizeEsimPackage(source);

  if (!normalizedPackage) {
    return [];
  }

  return [
    {
      key: "qr",
      title: "QR installation",
      html: normalizedPackage.qrInstallation,
    },
    {
      key: "manual",
      title: "Manual installation",
      html: normalizedPackage.manualInstallation,
    },
  ].filter((section) => stripHtml(section.html).trim().length > 0);
}

export function getLocalCountryByCode(code) {
  if (!code) {
    return null;
  }

  const match = countries.find(
    (country) => country.code?.toLowerCase() === String(code).toLowerCase(),
  );

  if (!match) {
    return null;
  }

  return {
    ...match,
    title: match.name,
    packageCount: 0,
    cheapestPrice: null,
  };
}

export function storeSelectedEsimCountry(country) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!country) {
      window.sessionStorage.removeItem(SELECTED_ESIM_COUNTRY_STORAGE_KEY);
      return;
    }

    window.sessionStorage.setItem(
      SELECTED_ESIM_COUNTRY_STORAGE_KEY,
      JSON.stringify(country),
    );
  } catch (error) {}
}

export function getStoredEsimCountry() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedValue = window.sessionStorage.getItem(
      SELECTED_ESIM_COUNTRY_STORAGE_KEY,
    );

    if (!storedValue) {
      return null;
    }

    return normalizeEsimCountry(JSON.parse(storedValue));
  } catch (error) {
    return null;
  }
}

function currencySymbolFor(currencyCode) {
  switch (currencyCode) {
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "ETB":
      return "Br ";
    case "NGN":
      return "₦";
    case "USD":
    default:
      return "$";
  }
}

function getLocalCountryByName(name) {
  if (!name) {
    return null;
  }

  const normalizedName = String(name).trim().toLowerCase();
  const match = countries.find(
    (country) => country.name?.trim().toLowerCase() === normalizedName,
  );

  if (!match) {
    return null;
  }

  return {
    ...match,
    title: match.name,
    packageCount: 0,
    cheapestPrice: null,
  };
}

function getValueAtPath(source, path) {
  return path.split(".").reduce((current, key) => current?.[key], source);
}

function findFirstArray(value, depth = 0) {
  if (depth > 6 || value == null) {
    return null;
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "object") {
    return null;
  }

  const preferredKeys = ["data", "items", "results", "countries", "packages"];
  for (const key of preferredKeys) {
    const nestedValue = findFirstArray(value[key], depth + 1);
    if (nestedValue) {
      return nestedValue;
    }
  }

  for (const nestedValue of Object.values(value)) {
    const result = findFirstArray(nestedValue, depth + 1);
    if (result) {
      return result;
    }
  }

  return null;
}

function resolveText(value) {
  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (!value || typeof value !== "object") {
    return "";
  }

  return resolveText(
    value.title ||
      value.name ||
      value.label ||
      value.value ||
      value.operator ||
      value.supplier ||
      value.country ||
      value.description,
  );
}

function normalizeNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = Number(value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  if (!value || typeof value !== "object") {
    return null;
  }

  return normalizeNumber(
    value.amount ||
      value.total ||
      value.value ||
      value.price ||
      value.farePrice ||
      value.grandTotal,
  );
}

function normalizeInteger(value) {
  const normalizedValue = normalizeNumber(value);

  if (!Number.isFinite(normalizedValue)) {
    return null;
  }

  return Math.round(normalizedValue);
}

function normalizeBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value > 0;
  }

  if (typeof value === "string") {
    return ["true", "yes", "1", "topup", "available"].includes(
      value.trim().toLowerCase(),
    );
  }

  return false;
}

function formatPackageData(value, unlimited) {
  const resolvedValue = resolveText(value);

  if (unlimited && !resolvedValue) {
    return "Unlimited data";
  }

  if (!resolvedValue) {
    return unlimited ? "Unlimited data" : "Data plan";
  }

  if (unlimited && !/unlimited/i.test(resolvedValue)) {
    return `Unlimited / ${resolvedValue}`;
  }

  const numericValue = Number(resolvedValue);
  if (Number.isFinite(numericValue)) {
    return `${numericValue} GB`;
  }

  return resolvedValue;
}

function formatCoverage(coverage) {
  const coverageList = Array.isArray(coverage) ? coverage : [coverage];
  return uniqueTextList(coverageList).join(", ");
}

function uniqueTextList(...values) {
  const collected = [];

  values.forEach((value) => {
    collectTextValues(collected, value);
  });

  return [...new Set(collected.map((item) => item.trim()).filter(Boolean))];
}

function collectTextValues(target, value) {
  if (value == null || value === false) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => collectTextValues(target, entry));
    return;
  }

  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    if (normalized) {
      target.push(normalized);
    }
    return;
  }

  if (typeof value !== "object") {
    return;
  }

  const prioritizedText = resolveText(
    value.name ||
      value.title ||
      value.label ||
      value.value ||
      value.operator ||
      value.network ||
      value.country ||
      value.description,
  );

  if (prioritizedText) {
    target.push(prioritizedText);
    return;
  }

  Object.values(value).forEach((entry) => collectTextValues(target, entry));
}

function inferNetworkTypeFromText(values) {
  const source = values.join(" ").toLowerCase();

  if (source.includes("5g")) {
    return "5G";
  }

  if (source.includes("4g")) {
    return "4G";
  }

  if (source.includes("lte")) {
    return "LTE";
  }

  return "";
}

function formatStructuredText(value, depth = 0) {
  if (value == null || value === false || depth > 3) {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => formatStructuredText(entry, depth + 1))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value !== "object") {
    return "";
  }

  return Object.entries(value)
    .map(([key, entryValue]) => {
      const text = formatStructuredText(entryValue, depth + 1);
      return text ? `${humanizeKey(key)}: ${text}` : "";
    })
    .filter(Boolean)
    .join(" • ");
}

function formatListValue(value, separator = ", ") {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(separator);
  }

  return resolveText(value);
}

function hasDisplayValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return Boolean(resolveText(value));
}

function stripHtml(value) {
  return String(value || "").replace(/<[^>]*>/g, " ");
}

function humanizeKey(value) {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());
}

function ensureObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}
